import React, { useState, useEffect } from 'react';
import { 
  FiPlus, FiSettings, FiActivity, FiGlobe, FiDatabase, 
  FiCheck, FiX, FiPlay, FiPause, FiEdit, FiTrash2,
  FiExternalLink, FiRefreshCw, FiAlertCircle, FiZap
} from 'react-icons/fi';

interface Integration {
  id: string;
  type: 'n8n' | 'google_sheets' | 'zapier' | 'webhook' | 'api' | 'custom';
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'error' | 'pending';
  config: any;
  lastSync?: Date;
  createdAt: Date;
  stats?: {
    totalExecutions: number;
    successRate: number;
    lastExecution: Date;
  };
}

interface WebhookConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH';
  headers: Record<string, string>;
  events: string[];
  secret?: string;
}

export const IntegrationManagement: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'logs'>('list');
  const [integrationType, setIntegrationType] = useState<'n8n' | 'google_sheets' | 'webhook' | 'custom'>('n8n');

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    // Mock data for now
    const mockIntegrations: Integration[] = [
      {
        id: '1',
        type: 'n8n',
        name: 'Main N8N Instance',
        description: 'Primary automation workflows for customer onboarding and notifications',
        status: 'active',
        config: {
          url: 'https://n8n.example.com',
          workflows: [
            { id: 'wf_001', name: 'Customer Onboarding', trigger: 'customer.created' },
            { id: 'wf_002', name: 'Invoice Notifications', trigger: 'invoice.sent' }
          ]
        },
        lastSync: new Date('2024-01-15T10:30:00'),
        createdAt: new Date('2024-01-10T08:00:00'),
        stats: {
          totalExecutions: 1250,
          successRate: 98.5,
          lastExecution: new Date('2024-01-15T10:30:00')
        }
      },
      {
        id: '2',
        type: 'google_sheets',
        name: 'Customer Data Sync',
        description: 'Bidirectional sync with customer management spreadsheet',
        status: 'active',
        config: {
          spreadsheetId: '1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms',
          sheetMappings: [
            { sheetName: 'Customers', entity: 'customers' },
            { sheetName: 'Leads', entity: 'leads' }
          ]
        },
        lastSync: new Date('2024-01-15T09:00:00'),
        createdAt: new Date('2024-01-12T14:30:00'),
        stats: {
          totalExecutions: 48,
          successRate: 100,
          lastExecution: new Date('2024-01-15T09:00:00')
        }
      }
    ];
    
    setIntegrations(mockIntegrations);
  };

  const integrationTypes = [
    {
      type: 'n8n',
      label: 'n8n Automation',
      icon: FiZap,
      description: 'Connect to n8n for workflow automation',
      color: 'bg-purple-500'
    },
    {
      type: 'google_sheets',
      label: 'Google Sheets',
      icon: FiDatabase,
      description: 'Sync data with Google Sheets',
      color: 'bg-green-500'
    },
    {
      type: 'webhook',
      label: 'Webhooks',
      icon: FiGlobe,
      description: 'Send data to external URLs',
      color: 'bg-blue-500'
    },
    {
      type: 'custom',
      label: 'Custom API',
      icon: FiSettings,
      description: 'Custom API integration',
      color: 'bg-gray-500'
    }
  ];

  const getStatusColor = (status: Integration['status']) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const testIntegration = async (integrationId: string) => {
    try {
      // Simulate API call to test integration
      console.log('Testing integration:', integrationId);
      // Update integration status
    } catch (error) {
      console.error('Error testing integration:', error);
    }
  };

  const toggleIntegration = async (integrationId: string, active: boolean) => {
    try {
      setIntegrations(prev => 
        prev.map(integration => 
          integration.id === integrationId 
            ? { ...integration, status: active ? 'active' : 'inactive' }
            : integration
        )
      );
    } catch (error) {
      console.error('Error toggling integration:', error);
    }
  };

  const deleteIntegration = async (integrationId: string) => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      setIntegrations(prev => prev.filter(integration => integration.id !== integrationId));
    }
  };

  const IntegrationCard: React.FC<{ integration: Integration }> = ({ integration }) => {
    const typeInfo = integrationTypes.find(t => t.type === integration.type);
    
    return (
      <div className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${typeInfo?.color} text-white`}>
              {typeInfo && <typeInfo.icon size={20} />}
            </div>
            <div>
              <h3 className="font-semibold text-lg">{integration.name}</h3>
              <p className="text-gray-600 text-sm">{integration.description}</p>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(integration.status)}`}>
            {integration.status}
          </span>
        </div>

        {integration.stats && (
          <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded">
            <div className="text-center">
              <div className="font-semibold text-blue-600">{integration.stats.totalExecutions}</div>
              <div className="text-xs text-gray-600">Executions</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-green-600">{integration.stats.successRate}%</div>
              <div className="text-xs text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-600">
                {integration.stats.lastExecution.toLocaleDateString()}
              </div>
              <div className="text-xs text-gray-600">Last Run</div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <button
              onClick={() => testIntegration(integration.id)}
              className="flex items-center space-x-1 px-3 py-1 text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
            >
              <FiPlay size={14} />
              <span>Test</span>
            </button>
            <button
              onClick={() => setSelectedIntegration(integration)}
              className="flex items-center space-x-1 px-3 py-1 text-gray-600 border rounded hover:bg-gray-50"
            >
              <FiEdit size={14} />
              <span>Edit</span>
            </button>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => toggleIntegration(integration.id, integration.status !== 'active')}
              className={`p-2 rounded ${
                integration.status === 'active' 
                  ? 'text-orange-600 hover:bg-orange-50' 
                  : 'text-green-600 hover:bg-green-50'
              }`}
            >
              {integration.status === 'active' ? <FiPause size={16} /> : <FiPlay size={16} />}
            </button>
            <button
              onClick={() => deleteIntegration(integration.id)}
              className="p-2 text-red-600 hover:bg-red-50 rounded"
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const CreateIntegrationDialog = () => {
    const [formData, setFormData] = useState<any>({});
    const [isCreating, setIsCreating] = useState(false);

    const handleCreate = async () => {
      setIsCreating(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const newIntegration: Integration = {
          id: Date.now().toString(),
          type: integrationType,
          name: formData.name || `New ${integrationType}`,
          description: formData.description || '',
          status: 'pending',
          config: formData,
          createdAt: new Date()
        };
        
        setIntegrations(prev => [...prev, newIntegration]);
        setShowCreateDialog(false);
        setFormData({});
      } catch (error) {
        console.error('Error creating integration:', error);
      } finally {
        setIsCreating(false);
      }
    };

    if (!showCreateDialog) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold">Create New Integration</h2>
            <button
              onClick={() => setShowCreateDialog(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX size={24} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Integration Type Selection */}
            <div>
              <label className="block text-sm font-medium mb-3">Integration Type</label>
              <div className="grid grid-cols-2 gap-3">
                {integrationTypes.map((type) => (
                  <button
                    key={type.type}
                    onClick={() => setIntegrationType(type.type as any)}
                    className={`p-4 border rounded-lg text-left hover:border-blue-500 ${
                      integrationType === type.type 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded ${type.color} text-white`}>
                        <type.icon size={16} />
                      </div>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-sm text-gray-600">{type.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Enter integration name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Enter description"
                />
              </div>
            </div>

            {/* Type-specific Configuration */}
            {integrationType === 'n8n' && (
              <div className="space-y-4">
                <h3 className="font-medium">N8N Configuration</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">N8N URL</label>
                    <input
                      type="url"
                      value={formData.n8nUrl || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, n8nUrl: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="https://n8n.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">API Key</label>
                    <input
                      type="password"
                      value={formData.apiKey || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, apiKey: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Enter API key"
                    />
                  </div>
                </div>
              </div>
            )}

            {integrationType === 'google_sheets' && (
              <div className="space-y-4">
                <h3 className="font-medium">Google Sheets Configuration</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Spreadsheet ID</label>
                    <input
                      type="text"
                      value={formData.spreadsheetId || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, spreadsheetId: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Service Account Key (JSON)</label>
                    <textarea
                      value={formData.serviceAccountKey || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, serviceAccountKey: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      rows={4}
                      placeholder="Paste your service account JSON key here"
                    />
                  </div>
                </div>
              </div>
            )}

            {integrationType === 'webhook' && (
              <div className="space-y-4">
                <h3 className="font-medium">Webhook Configuration</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Webhook URL</label>
                    <input
                      type="url"
                      value={formData.url || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="https://example.com/webhook"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">HTTP Method</label>
                    <select
                      value={formData.method || 'POST'}
                      onChange={(e) => setFormData(prev => ({ ...prev, method: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Secret (Optional)</label>
                    <input
                      type="password"
                      value={formData.secret || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, secret: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Webhook secret for signature verification"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between p-6 border-t bg-gray-50">
            <button
              onClick={() => setShowCreateDialog(false)}
              className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating || !formData.name}
              className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center space-x-2"
            >
              {isCreating && <FiRefreshCw size={16} className="animate-spin" />}
              <span>{isCreating ? 'Creating...' : 'Create Integration'}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Integration Management</h1>
          <p className="text-gray-600">Connect your ERP with external tools and services</p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          <FiPlus size={16} />
          <span>Add Integration</span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b mb-6">
        <nav className="flex space-x-8">
          {[
            { key: 'list', label: 'Integrations', icon: FiGlobe },
            { key: 'logs', label: 'Activity Logs', icon: FiActivity }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'list' && (
        <div className="space-y-6">
          {integrations.length === 0 ? (
            <div className="text-center py-12">
              <FiGlobe size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No Integrations</h3>
              <p className="text-gray-500 mb-4">Get started by adding your first integration</p>
              <button
                onClick={() => setShowCreateDialog(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Add Integration
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {integrations.map((integration) => (
                <IntegrationCard key={integration.id} integration={integration} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg">
            <div className="p-4 border-b">
              <h3 className="font-medium">Recent Activity</h3>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {[
                  { time: '2 minutes ago', action: 'N8N workflow executed', status: 'success' },
                  { time: '15 minutes ago', action: 'Google Sheets sync completed', status: 'success' },
                  { time: '1 hour ago', action: 'Webhook delivery failed', status: 'error' },
                  { time: '2 hours ago', action: 'Customer data exported', status: 'success' }
                ].map((log, index) => (
                  <div key={index} className="flex items-center space-x-3 py-2">
                    <div className={`w-2 h-2 rounded-full ${
                      log.status === 'success' ? 'bg-green-400' : 'bg-red-400'
                    }`} />
                    <div className="flex-1">
                      <div className="text-sm">{log.action}</div>
                      <div className="text-xs text-gray-500">{log.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <CreateIntegrationDialog />
    </div>
  );
}; 